import { useState } from "react";

import { borrarParaSiempre, PAPELERA_DIAS, planillasEnPapelera, restaurarDePapelera } from "../services/espacio";
import { diaAr } from "../shared/fechaAr";
import "./css/espacio-historial.css";

/* La papelera de las planillas de Mi espacio.
 *
 * «Borrar planilla» ya no es para siempre: la manda acá. El dueño la restaura
 * (vuelve a su lista y a la de quienes la tenían compartida) o la borra de
 * verdad. A los PAPELERA_DIAS la borra sola la base (pg_cron, todas las
 * madrugadas): no hace falta que nadie abra esto para que se vacíe.
 *
 * Se lee recién al desplegarla: la lista de Mi espacio no paga una consulta
 * más por algo que se mira de vez en cuando. `onRestaurada()` le avisa a la
 * lista que tiene una planilla de vuelta.
 */

const DIA_MS = 86400000;

/* Cuántos días le quedan antes de que la purga se la lleve (0 = esta noche). */
export function diasQueQuedan(borradaEn, ahora = Date.now(), dias = PAPELERA_DIAS || 30) {
  const t = new Date(borradaEn).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.ceil((t + dias * DIA_MS - ahora) / DIA_MS));
}

function textoQuedan(n) {
  if (n === null) return "";
  if (n === 0) return "se borra sola esta noche";
  return n === 1 ? "se borra sola en 1 día" : `se borra sola en ${n} días`;
}

export default function PapeleraPlanillas({ onRestaurada }) {
  const [abierta, setAbierta] = useState(false);
  const [lista, setLista] = useState(null);
  const [error, setError] = useState(null);
  const [trabajando, setTrabajando] = useState(null); // id en curso

  const leer = () => {
    setError(null);
    setLista(null);
    planillasEnPapelera()
      .then(setLista)
      .catch(() => setError("No se pudo consultar."));
  };
  const alternar = () => {
    if (!abierta) leer();
    setAbierta((a) => !a);
  };

  const restaurar = async (p) => {
    setTrabajando(p.id);
    setError(null);
    try {
      await restaurarDePapelera(p.id);
      setLista((l) => (l || []).filter((x) => x.id !== p.id));
      if (onRestaurada) onRestaurada(p.id);
    } catch {
      setError(`No se pudo restaurar «${p.nombre || "Sin título"}».`);
    } finally {
      setTrabajando(null);
    }
  };

  const borrar = async (p) => {
    if (!window.confirm(
      `¿Borrar para siempre «${p.nombre || "Sin título"}»? Se va con todo su historial y no se puede deshacer.`,
    )) return;
    setTrabajando(p.id);
    setError(null);
    try {
      await borrarParaSiempre(p.id);
      setLista((l) => (l || []).filter((x) => x.id !== p.id));
    } catch {
      setError(`No se pudo borrar «${p.nombre || "Sin título"}».`);
    } finally {
      setTrabajando(null);
    }
  };

  return (
    <section className="esph-papelera">
      <button className="esp-btn" onClick={alternar} aria-expanded={abierta}>
        🗑 Papelera {abierta ? "▲" : "▼"}
      </button>
      {abierta && (
        <div className="esph-papelera-cuerpo">
          <p className="tenue">
            Las planillas que borrás quedan acá {PAPELERA_DIAS || 30} días y después
            se borran solas. Mientras están acá, nadie más las ve.
          </p>
          {error && <p className="panel-aviso" role="alert">{error}</p>}
          {lista === null && !error && <p className="tenue">Cargando…</p>}
          {lista && lista.length === 0 && <p className="tenue">La papelera está vacía.</p>}
          {lista && lista.length > 0 && (
            <ul className="esph-lista">
              {lista.map((p) => (
                <li key={p.id}>
                  <span className="esph-cuando">
                    <b>{p.nombre || "Sin título"}</b>
                    <span className="tenue">
                      borrada el {diaAr(p.borrada_en)} · {textoQuedan(diasQueQuedan(p.borrada_en))}
                    </span>
                  </span>
                  <span className="esph-botones">
                    <button
                      className="esp-btn chico" disabled={!!trabajando} onClick={() => restaurar(p)}
                      aria-label={`Restaurar ${p.nombre || "Sin título"}`}
                    >Restaurar</button>
                    <button
                      className="esp-btn chico esph-peligro" disabled={!!trabajando} onClick={() => borrar(p)}
                      aria-label={`Borrar para siempre ${p.nombre || "Sin título"}`}
                    >Borrar para siempre</button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
